import { CSSRewriter } from './cssrewriter';
import { toNormalizedAttributeName, toNormalizedTagName } from './normalizedstring';
import { NodeRewriter } from './noderewriter';
import { UrlRewriter } from '@fullstory/url-rewriter';

// Tags that are forbidden within SVG resource documents.
const BlockedSvgResourceDocTags = /embed|iframe|noscript|object|script/i;

// An object that can rewrite SVG resource documents. Any scripts or event
// handlers are removed, and many of the same transformations we apply to HTML
// documents are performed.
// TODO(sethf): It would be ideal to fully unify the handling of HTML and SVG
// documents by creating an FsDocument for SVG resource documents. We'd also
// like to be able to support recursive references to other SVG resource
// documents. Both of these things are difficult when we're constructing blob
// URIs for child documents because the child documents' URL implicitly depends
// on their contents, which creates some tricky dependencies. The service worker
// breaks that dependency because we can use the original URL for rewritten
// documents, so the problem should go away soon.
export class SvgRewriter {
  constructor(
    private _cssRewriter: CSSRewriter,
    private _urlRewriter: UrlRewriter,
  ) {
  }

  // Rewrites an SVG resource document represented as a DOM tree.
  rewriteSvgDocument(resource: Document, baseUrl: string): string {
    const nodeRewriter =
      new NodeRewriter(this._cssRewriter, this._urlRewriter);

    const allElements = resource.querySelectorAll('*');
    for (const elem of allElements) {
      // Remove sketchy elements.
      const tag = toNormalizedTagName(elem.tagName, 'svg');
      if (BlockedSvgResourceDocTags.test(tag)) {
        console.error(`Removing an unsafe <${tag}> element from SVG ` +
          `resource document: ${baseUrl}`);
        if (!elem.parentNode) {
          throw new Error(`Couldn't remove unsafe <${tag}> element`);
        }
        elem.parentNode.removeChild(elem);
        continue;
      }

      // Rewrite attributes, and remember ones that we want to remove. We wait
      // until we're done iterating through them to remove anything since
      // Element.prototype.attributes() returns a live collection.
      const attrNodesToRemove: Attr[] = [];
      for (const attrNode of elem.attributes) {
        const attr = toNormalizedAttributeName(attrNode.localName, 'svg');
        const value = attrNode.value;

        const rewrittenValue = nodeRewriter.rewriteAttr(tag, attr, value);
        if (rewrittenValue === null) {
          attrNodesToRemove.push(attrNode);
        } else {
          attrNode.value = rewrittenValue;
        }
      }

      // Get rid of attributes that we wanted to remove.
      for (const attrNode of attrNodesToRemove) {
        elem.removeAttributeNode(attrNode);
      }
    }

    // Serialize the rewritten document.
    const serializer = new XMLSerializer();
    return serializer.serializeToString(resource);
  }

  // Rewrites an SVG resource document represented as a string.
  rewriteSvgDocumentText(text: string, baseUrl: string): string {
    const parser = new DOMParser();
    const resource = parser.parseFromString(text, 'image/svg+xml');
    return this.rewriteSvgDocument(resource, baseUrl);
  }
}
